import React, { useContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import connect from '../../../utils/connect';
import filterActions from '../../../actions/filters';

import { FiltersFormContext } from './FiltersFormContext';
import {
    SOCIAL_PLATFORMS_OPTIONS,
    BROADCAST_PLATFORM_OPTIONS,
    GAME_PLATFORM_OPTIONS,
} from '../../../insights/constants';

import { useFormikContext } from 'formik';
import { BROADCAST, IN_VENUE } from '../../../constants/platformIds';
import getSelectedGroupByOptions from '../../../utils/getSelectedGroupByOptions';
import { getCompareOptions } from '../insights/getOptions';
import { getCompareDatesForOption } from '../insights/compareDateUtils';

import { Typography, Flex, Select, theme, Tooltip } from '@app/ui';

const InsightsGroupByRow = ({ client, sendNotifications }) => {
    const { t } = useTranslation(['measurement', 'common']);
    const { values, setFieldValue } = useFormikContext();
    const [canSetDefault, setCanSetDefault] = useState(true);

    const { getInsightsGroupByOptions, setIsCountrySelectionDisabled } =
        useContext(FiltersFormContext);

    const groupByOptions = getInsightsGroupByOptions();

    const { startDate, endDate, compareDates } = values;
    const hasBroadcast = client.platformIds.includes(BROADCAST);
    const hasInVenue = client.platformIds.includes(IN_VENUE);

    // Handle changes to groupBy and update platform options accordingly
    React.useEffect(() => {
        const selectedGroupBy = values.insightsGroupBy;

        if (selectedGroupBy.socialAccountGroup) {
            setFieldValue('platforms', SOCIAL_PLATFORMS_OPTIONS, false);
        } else if (selectedGroupBy.mediaUploadName) {
            setFieldValue('platforms', BROADCAST_PLATFORM_OPTIONS, false);
        } else if (selectedGroupBy.gameName) {
            const isBroadcastSelected = values.platforms?.[BROADCAST];
            const isInVenueSelected = values.platforms?.[IN_VENUE];
            if (!isBroadcastSelected && !isInVenueSelected) {
                setFieldValue(
                    'platforms',
                    GAME_PLATFORM_OPTIONS(hasBroadcast, hasInVenue),
                    false,
                );
            }
        }
    }, [values.insightsGroupBy, setFieldValue]);

    const updateGroupBy = newGroupBy => {
        const newGroupByObj = newGroupBy.reduce((acc, value) => {
            acc[value] = true;
            return acc;
        }, {});
        setFieldValue('insightsGroupBy', newGroupByObj, false);

        setCanSetDefault(true);
    };

    const {
        token: { colorPrimary },
    } = theme.useToken();

    // Filter groupBy options based on current selection
    const filteredGroupByOptions = useMemo(() => {
        const selectedGroupBy = values.insightsGroupBy;
        if (selectedGroupBy.socialAccountGroup) {
            return groupByOptions.filter(
                option => option.value !== 'mediaUploadName',
            );
        }
        if (selectedGroupBy.mediaUploadName) {
            return groupByOptions.filter(
                option => option.value !== 'socialAccountGroup',
            );
        }
        return groupByOptions;
    }, [groupByOptions, values.insightsGroupBy]);

    const compareOptions = React.useMemo(
        () =>
            getCompareOptions(
                {
                    startDate,
                    endDate: endDate || new Date(),
                },
                'label',
                true,
                t,
            ),
        [startDate, endDate, t],
    );

    const onChangeOption = React.useCallback(
        selected => {
            if (!selected) {
                setFieldValue(
                    'compareDates',
                    {
                        option: undefined,
                        dates: undefined,
                    },
                    false,
                );
                return;
            }
            const compareDates = {
                option: selected,
                dates: getCompareDatesForOption({
                    startDate,
                    endDate: endDate || new Date(),
                    option: selected,
                }),
            };
            setFieldValue('compareDates', compareDates, false);
        },
        [startDate, endDate],
    );

    const setDefaultGroupBy = () => {
        const groupByList = getSelectedGroupByOptions(values.insightsGroupBy);
        if (!groupByList.length) {
            alert(t('common:filters.groupByRequired'));
            return;
        } else {
            localStorage.setItem('storedGroupBy', JSON.stringify(groupByList));
            setCanSetDefault(false);
            sendNotifications(t('common:filters.defaultGroupByUpdated'));
        }
    };

    const groupBys = Object.entries(values.insightsGroupBy)
        .filter(([, value]) => value)
        .map(([key]) => key);

    React.useEffect(() => {
        if (values.insightsGroupBy.socialAccountGroup) {
            setIsCountrySelectionDisabled(true);
        } else {
            setIsCountrySelectionDisabled(false);
        }
    }, [values.insightsGroupBy, setIsCountrySelectionDisabled]);

    return (
        <Flex gap={12} flex={1}>
            <Flex vertical flex={1} gap={4}>
                <Typography.Text className="-filter-title">
                    {t('common:filters.groupBy')}
                    {canSetDefault && (
                        <Typography.Link
                            onClick={setDefaultGroupBy}
                            style={{
                                color: colorPrimary,
                                marginLeft: '12px',
                                fontSize: 12,
                                fontWeight: 600,
                            }}
                        >
                            {t('common:filters.setAsDefaultGroupBy')}
                        </Typography.Link>
                    )}
                </Typography.Text>

                <Select
                    mode="multiple"
                    placeholder={t('common:filters.selectGroupBy')}
                    options={filteredGroupByOptions}
                    autoClearSearchValue={false}
                    value={groupBys || []}
                    onChange={updateGroupBy}
                    optionFilterProp="label"
                    allowClear
                    maxTagCount={3}
                    maxTagPlaceholder={`...`}
                    style={{
                        maxHeight: '32px',
                        overflow: 'hidden',
                    }}
                    disabled={filteredGroupByOptions.length === 0}
                />
            </Flex>
            <Flex vertical flex={1} gap={4}>
                <Typography.Text
                    className="-filter-title"
                    data-test="new-cd-dropdown"
                >
                    {t('common:filters.compareDates')}
                </Typography.Text>
                <Tooltip
                    title={
                        !startDate || compareOptions.length === 0
                            ? t('common:filters.selectDateRangeFirst')
                            : undefined
                    }
                    placement="top"
                >
                    <Select
                        placeholder={t('common:filters.selectCompareDates')}
                        options={compareOptions}
                        onChange={onChangeOption}
                        value={compareDates?.option}
                        defaultValue={compareDates?.option || null}
                        optionFilterProp="label"
                        allowClear
                        maxTagPlaceholder={`...`}
                        style={{
                            maxHeight: '32px',
                            overflow: 'hidden',
                        }}
                        disabled={!startDate || compareOptions.length === 0}
                    />
                </Tooltip>
            </Flex>
        </Flex>
    );
};

InsightsGroupByRow.propTypes = {
    client: PropTypes.object.isRequired,
    sendNotifications: PropTypes.func,
};

export default connect(['client'], filterActions)(InsightsGroupByRow);
